import { Boxes, Loader2 } from "lucide-react"
import type { KeyboardEvent, ReactNode } from "react"
import {
  SCALE_SHEET_SELECTION_ACTIVE_BORDER,
  SCALE_SHEET_SELECTION_REST_BORDER,
} from "@/components/scale-sheet/scale-sheet-height-resize-handle"
import { AssetRegenerateOverlayButton } from "@/components/media/asset-media-overlay"
import { assetPreviewBusyImageClass } from "@/components/media/asset-preview-busy"
import { openImageInNewTab } from "@/lib/download-image"
import { GLITCH_PLACEHOLDER_URL } from "@/lib/placeholders"
import { cn } from "@/lib/utils"
import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  formatLabelCm,
  PREVIEW_LABEL_RESERVE,
  SCALE_SHEET_CANVAS_BG,
  SCALE_SHEET_SERVER_PREVIEW_MAX_PX,
  scaleSheetFigureEntityKey,
  type ScaleSheetPlacedFigure,
} from "@/lib/scale-sheet-layout"

type PlaceholderSurface = "sunken" | "canvas"
type MissingFigurePlaceholder = "character" | "prop"

/** Same grey fill as glitch-placeholder.png silhouette */
const PROP_MISSING_FIGURE_ICON_COLOR = "#A9A9A9"

/** Layout px between figure bottom and label text in live preview */
const PREVIEW_LABEL_IMAGE_GAP = 8

type Props = {
  figures: ScaleSheetPlacedFigure[]
  contentWidth: number
  activeEntityKey?: string | null
  onHoverEntity?: (entityKey: string) => void
  onUnhoverEntity?: () => void
  onTapEntity?: (entityKey: string) => void
  onTapBackground?: () => void
  onServerImageClick?: () => void
  serverImageUrl?: string | null
  showServerImage?: boolean
  loading?: boolean
  showBusyOverlay?: boolean
  compactPreviewHeight?: boolean
  compactServerImage?: boolean
  serverImageOpenUrl?: string | null
  previewDisplayZone?: { top: number; bottom: number }
  placeholderSurface?: PlaceholderSurface
  missingFigurePlaceholder?: MissingFigurePlaceholder
  canvasOverlay?: ReactNode
  selectionResizeActive?: boolean
  framePaddingY?: number
  fixedFrameHeightPx?: number
  onRegenerateFigure?: (entityKey: string) => void
  regenerateFigureTitle?: string | ((entityKey: string) => string)
  isFigureBusy?: (entityKey: string) => boolean
  figureGenerateDisabled?: (entityKey: string) => { disabled: boolean; reason?: string }
}

function renderFigure(
  fig: ScaleSheetPlacedFigure,
  entityKey: string,
  layoutWidth: number,
  displayZoneTop: number,
  displayZoneHeight: number,
  highlighted: boolean,
  highlightedInteractive: boolean,
  interactiveResize: boolean,
  figureLabel: string,
  heightLabel: string,
  selectionResizeActive: boolean,
  canvasOverlay: ReactNode,
  missingFigurePlaceholder: MissingFigurePlaceholder,
  onHoverEntity?: (entityKey: string) => void,
  onUnhoverEntity?: () => void,
  onTapEntity?: (entityKey: string) => void,
  onRegenerateFigure?: (entityKey: string) => void,
  regenerateFigureTitle?: string | ((entityKey: string) => string),
  isFigureBusy?: (entityKey: string) => boolean,
  figureGenerateDisabled?: (entityKey: string) => { disabled: boolean; reason?: string },
) {
  const leftPct = (fig.x / layoutWidth) * 100
  const wPct = (fig.w / layoutWidth) * 100
  const blockTopPct = ((fig.y - displayZoneTop) / displayZoneHeight) * 100
  const blockHeightPct = ((fig.h + PREVIEW_LABEL_RESERVE) / displayZoneHeight) * 100
  const blockHeightLayout = fig.h + PREVIEW_LABEL_RESERVE
  const imageHeightPct = (fig.h / blockHeightLayout) * 100
  const labelTopPct = ((fig.h + PREVIEW_LABEL_IMAGE_GAP) / blockHeightLayout) * 100
  const treatAsProp = (fig.entity_type ?? (missingFigurePlaceholder === "prop" ? "prop" : "character")) === "prop"
  const showPropIcon = !fig.scale_pose_url && treatAsProp
  const generateDisabled = figureGenerateDisabled?.(entityKey)
  const figureBusy = Boolean(isFigureBusy?.(entityKey))

  return (
    <div
      key={entityKey}
      role={interactiveResize ? "group" : undefined}
      tabIndex={interactiveResize ? 0 : undefined}
      className={cn(
        "absolute min-w-0 overflow-visible text-left outline-none",
        interactiveResize ? "cursor-default" : "cursor-pointer",
      )}
      style={{
        left: `${leftPct}%`,
        top: `${blockTopPct}%`,
        width: `${wPct}%`,
        height: `${blockHeightPct}%`,
      }}
      aria-label={figureLabel}
      onMouseEnter={interactiveResize ? () => onHoverEntity?.(entityKey) : undefined}
      onMouseLeave={interactiveResize ? onUnhoverEntity : undefined}
      onFocus={interactiveResize ? () => onHoverEntity?.(entityKey) : undefined}
      onClick={
        interactiveResize
          ? (e) => {
              e.stopPropagation()
              onTapEntity?.(entityKey)
            }
          : undefined
      }
      onKeyDown={
        interactiveResize
          ? (e: KeyboardEvent<HTMLDivElement>) => {
              if (e.key !== "Enter" && e.key !== " ") return
              e.preventDefault()
              e.stopPropagation()
              onTapEntity?.(entityKey)
            }
          : undefined
      }
    >
      <div
        className={cn("absolute inset-x-0 top-0", highlightedInteractive && "group")}
        style={{ height: `${imageHeightPct}%` }}
      >
        <div className="absolute inset-0 overflow-hidden">
          <div
            className={cn(
              "absolute right-0 bottom-0 left-0 mx-auto max-h-full w-full border-2 transition-colors",
              highlightedInteractive && SCALE_SHEET_SELECTION_REST_BORDER,
              highlightedInteractive && selectionResizeActive && SCALE_SHEET_SELECTION_ACTIVE_BORDER,
              highlighted && !interactiveResize && "border-ui-accent ring-1 ring-ui-accent/40",
              !highlighted && "border-transparent",
            )}
            style={{ aspectRatio: `${fig.w} / ${fig.h}` }}
          >
            {showPropIcon ? (
              <div
                className={cn(
                  "flex h-full w-full items-center justify-center",
                  highlightedInteractive && "pointer-events-none",
                )}
                aria-hidden
              >
                <Boxes
                  className="h-[40%] w-[40%]"
                  style={{ color: PROP_MISSING_FIGURE_ICON_COLOR }}
                  strokeWidth={1.5}
                />
              </div>
            ) : (
              <img
                src={fig.scale_pose_url || GLITCH_PLACEHOLDER_URL}
                alt=""
                className={cn(
                  "block h-full w-full",
                  !fig.scale_pose_url && "object-contain",
                  highlightedInteractive && "pointer-events-none",
                )}
                draggable={false}
              />
            )}
            {onRegenerateFigure ? (
              <div className="pointer-events-auto absolute top-1.5 left-1.5 z-40">
                <AssetRegenerateOverlayButton
                  title={
                    typeof regenerateFigureTitle === "function"
                      ? regenerateFigureTitle(entityKey)
                      : (regenerateFigureTitle ?? "")
                  }
                  busy={figureBusy}
                  disabled={generateDisabled?.disabled}
                  disabledReason={generateDisabled?.reason}
                  onClick={() => onRegenerateFigure(entityKey)}
                />
              </div>
            ) : null}
          </div>
        </div>
        {highlighted && canvasOverlay ? (
          <div
            className="pointer-events-none absolute right-0 bottom-0 left-0 mx-auto max-h-full w-full"
            style={{ aspectRatio: `${fig.w} / ${fig.h}` }}
          >
            {canvasOverlay}
          </div>
        ) : null}
      </div>
      <div
        className={cn(
          "absolute left-1/2 z-10 w-max max-w-none -translate-x-1/2 overflow-visible px-0.5 text-center",
          highlighted ? "font-semibold text-ui-accent" : "font-medium text-zinc-900",
        )}
        style={{ top: `${labelTopPct}%` }}
      >
        <span className="block text-[10px] leading-tight whitespace-nowrap">{fig.name}</span>
        <span className="mt-0.5 block text-[10px] leading-tight whitespace-nowrap tabular-nums">{heightLabel}</span>
      </div>
    </div>
  )
}

function ScaleSheetPreviewBusyOverlay({ showDim, showSpinner }: { showDim: boolean; showSpinner: boolean }) {
  if (!showDim && !showSpinner) return null
  return (
    <div
      className={cn(
        "pointer-events-auto absolute inset-0 z-30 flex items-center justify-center",
        showDim && "bg-ui-surface-sunken/60",
      )}
      aria-busy={showSpinner || undefined}
    >
      {showSpinner ? <Loader2 className="h-5 w-5 animate-spin text-ui-text-muted" /> : null}
    </div>
  )
}

export function ScaleSheetPreview({
  figures,
  contentWidth,
  activeEntityKey = null,
  onHoverEntity,
  onUnhoverEntity,
  onTapEntity,
  onTapBackground,
  onServerImageClick,
  serverImageUrl,
  showServerImage = false,
  loading = false,
  showBusyOverlay = true,
  compactPreviewHeight: _compactPreviewHeight = false,
  compactServerImage = false,
  serverImageOpenUrl = null,
  previewDisplayZone,
  placeholderSurface = "sunken",
  missingFigurePlaceholder = "character",
  canvasOverlay,
  selectionResizeActive = false,
  framePaddingY,
  fixedFrameHeightPx,
  onRegenerateFigure,
  regenerateFigureTitle,
  isFigureBusy,
  figureGenerateDisabled,
}: Props) {
  const interactiveResize = onHoverEntity != null || (activeEntityKey != null && canvasOverlay != null)
  const usesPreviewCoordinates = previewDisplayZone != null
  const previewLayoutWidth = usesPreviewCoordinates ? contentWidth : Math.max(contentWidth, CANVAS_WIDTH)
  const legacyInnerWidthPct = (previewLayoutWidth / CANVAS_WIDTH) * 100
  const canEnterEditFromImage = onServerImageClick != null && !loading
  const showCompactServer = showServerImage && compactServerImage && Boolean(serverImageUrl)
  const canOpenServerImage = Boolean(serverImageOpenUrl) && !loading && onServerImageClick == null
  const displayZoneTop = previewDisplayZone?.top ?? 0
  const displayZoneHeight = previewDisplayZone ? previewDisplayZone.bottom - displayZoneTop : CANVAS_HEIGHT
  const fitDisplayZoneInViewport = usesPreviewCoordinates && fixedFrameHeightPx != null
  const scrollPreviewRow = usesPreviewCoordinates && !fitDisplayZoneInViewport
  const displayZoneLayerStyle = usesPreviewCoordinates
    ? {
        aspectRatio: `${previewLayoutWidth} / ${displayZoneHeight}`,
        backgroundColor: SCALE_SHEET_CANVAS_BG,
        ...(fitDisplayZoneInViewport ? { width: "auto" as const } : undefined),
      }
    : undefined
  const hasVisiblePreview = (showServerImage && Boolean(serverImageUrl)) || (!showServerImage && figures.length > 0)
  const showLoadingPlaceholder = loading && !hasVisiblePreview
  const showBusyDim = loading && hasVisiblePreview
  const showBusySpinner = loading && showBusyOverlay && (hasVisiblePreview || showLoadingPlaceholder)
  const showBusyLayers = showBusyDim || showBusySpinner
  const busyImageClass = showBusyDim ? assetPreviewBusyImageClass : undefined

  const applyFramePadding =
    framePaddingY != null &&
    framePaddingY > 0 &&
    !showCompactServer &&
    !(showServerImage && serverImageUrl) &&
    !(showLoadingPlaceholder && placeholderSurface === "sunken")

  const viewportStyle = applyFramePadding
    ? {
        paddingTop: framePaddingY,
        paddingBottom: framePaddingY,
        backgroundColor: SCALE_SHEET_CANVAS_BG,
      }
    : undefined

  const figureLayer = (
    <div
      className={cn(
        "relative h-full w-full overflow-visible transition-opacity",
        showBusyDim && figures.length > 0 && assetPreviewBusyImageClass,
      )}
      onClick={interactiveResize ? onTapBackground : undefined}
    >
      {figures.map((fig) => {
        const entityKey = scaleSheetFigureEntityKey(fig)
        const highlighted = activeEntityKey === entityKey
        const highlightedInteractive = highlighted && interactiveResize
        const heightLabel = formatLabelCm(fig.height_cm)
        const figureLabel = `${fig.name}, ${heightLabel}`

        return renderFigure(
          fig,
          entityKey,
          previewLayoutWidth,
          displayZoneTop,
          displayZoneHeight,
          highlighted,
          highlightedInteractive,
          interactiveResize,
          figureLabel,
          heightLabel,
          selectionResizeActive,
          canvasOverlay,
          missingFigurePlaceholder,
          onHoverEntity,
          onUnhoverEntity,
          onTapEntity,
          onRegenerateFigure,
          regenerateFigureTitle,
          isFigureBusy,
          figureGenerateDisabled,
        )
      })}
    </div>
  )

  const previewContent = showLoadingPlaceholder ? (
    placeholderSurface === "canvas" ? (
      <div className="relative h-full min-h-[120px] w-full" style={{ backgroundColor: SCALE_SHEET_CANVAS_BG }} />
    ) : null
  ) : showCompactServer ? (
    <img
      src={serverImageUrl ?? undefined}
      alt=""
      className={cn(
        "mx-auto object-contain transition-opacity",
        (canOpenServerImage || canEnterEditFromImage) && "cursor-pointer",
        busyImageClass,
      )}
      style={{
        maxHeight: SCALE_SHEET_SERVER_PREVIEW_MAX_PX,
        maxWidth: SCALE_SHEET_SERVER_PREVIEW_MAX_PX,
      }}
      draggable={false}
      onClick={() => {
        if (canEnterEditFromImage) {
          onServerImageClick()
          return
        }
        if (!canOpenServerImage) return
        openImageInNewTab(serverImageOpenUrl)
      }}
    />
  ) : (
    <div
      className={cn(
        "relative h-full",
        fitDisplayZoneInViewport ? "flex w-full items-center justify-center" : undefined,
        scrollPreviewRow ? "w-max min-w-full" : undefined,
      )}
      style={
        fitDisplayZoneInViewport
          ? { backgroundColor: SCALE_SHEET_CANVAS_BG }
          : scrollPreviewRow
            ? { backgroundColor: SCALE_SHEET_CANVAS_BG }
            : {
                width: `${legacyInnerWidthPct}%`,
                minWidth: "100%",
                backgroundColor: SCALE_SHEET_CANVAS_BG,
              }
      }
    >
      {showServerImage && serverImageUrl ? (
        <img
          src={serverImageUrl}
          alt=""
          className={cn("h-full w-full object-contain object-left-top transition-opacity", busyImageClass)}
          draggable={false}
        />
      ) : usesPreviewCoordinates ? (
        <div
          className={cn(
            fitDisplayZoneInViewport ? "relative h-full max-w-full" : "h-full",
            scrollPreviewRow ? "w-auto" : "w-full",
          )}
          style={displayZoneLayerStyle}
        >
          {figureLayer}
        </div>
      ) : (
        <div className="relative h-full w-full">{figureLayer}</div>
      )}
    </div>
  )

  if (showCompactServer) {
    return (
      <div className="relative w-full">
        <div className="relative flex min-h-[120px] justify-center overflow-hidden rounded-lg border border-ui-border bg-ui-surface-sunken p-2">
          {previewContent}
          <ScaleSheetPreviewBusyOverlay showDim={showBusyDim} showSpinner={showBusySpinner} />
        </div>
      </div>
    )
  }

  return (
    <div className="relative w-full">
      <div
        className={cn(
          "relative w-full overflow-hidden rounded-lg border border-ui-border",
          fixedFrameHeightPx == null && "aspect-video",
        )}
        style={fixedFrameHeightPx != null ? { height: fixedFrameHeightPx } : undefined}
      >
        <div
          className={cn(
            "relative box-border h-full",
            showBusyLayers ? "overflow-hidden" : "overflow-x-auto overflow-y-hidden",
            showLoadingPlaceholder && placeholderSurface === "sunken" && "bg-ui-surface-sunken",
          )}
          style={viewportStyle}
        >
          {previewContent}
        </div>
        <ScaleSheetPreviewBusyOverlay showDim={showBusyDim} showSpinner={showBusySpinner} />
      </div>
    </div>
  )
}
